import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Star,
  Quote,
  ChevronLeft,
  ChevronRight,
  Award,
  ThumbsUp,
} from 'lucide-react';
import GlassCard from './GlassCard';
import { reviews } from '../data/reviews';

const Reviews = ({ lang = 'en' }) => {
  const isAr = lang === 'ar';
  const [currentIndex, setCurrentIndex] = useState(0);
  const [autoplay, setAutoplay] = useState(true);

  useEffect(() => {
    if (!autoplay) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % reviews.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [autoplay, reviews.length]);

  const content = {
    title: isAr ? 'آراء العملاء' : 'Customer Reviews',
    subtitle: isAr
      ? 'شاهد ما يقوله عملاؤنا عن خدماتنا المتميزة في غسيل السيارات'
      : 'See what our customers say about our premium car wash services',
    verified: isAr ? 'موثق' : 'Verified',
    stats: [
      {
        label: isAr ? 'متوسط التقييم' : 'Average Rating',
        value: '4.9/5',
        icon: <Star className='h-8 w-8 text-white' />,
        color: 'from-yellow-500 to-orange-500',
      },
      {
        label: isAr ? 'إجمالي المراجعات' : 'Total Reviews',
        value: '245+',
        icon: <Quote className='h-8 w-8 text-white' />,
        color: 'from-blue-500 to-cyan-500',
      },
      {
        label: isAr ? 'عملاء دائمون' : 'Repeat Customers',
        value: '85%',
        icon: <ThumbsUp className='h-8 w-8 text-white' />,
        color: 'from-green-500 to-emerald-500',
      },
    ],
  };

  const nextReview = () => {
    setAutoplay(false);
    setCurrentIndex((prev) => (prev + 1) % reviews.length);
  };

  const prevReview = () => {
    setAutoplay(false);
    setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }).map((_, index) => (
      <Star
        key={index}
        className={`h-5 w-5 ${
          index < rating
            ? 'fill-yellow-400 text-yellow-400'
            : 'fill-gray-300 dark:fill-gray-700 text-gray-300 dark:text-gray-700'
        }`}
      />
    ));
  };

  return (
    <section
      id='reviews'
      className='section-padding py-20'
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <div className='max-w-7xl mx-auto'>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className='text-center mb-12'
        >
          <h2 className='text-4xl md:text-5xl font-black mb-4 text-gradient'>
            {content.title}
          </h2>
          <p className='text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto'>
            {content.subtitle}
          </p>
        </motion.div>

        {/* Stats Grid */}
        <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-16'>
          {content.stats.map((stat, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <GlassCard
                lang={lang}
                className='p-6 text-center group hover:scale-105 transition-transform'
              >
                <div
                  className={`inline-flex p-4 bg-gradient-to-br ${stat.color} rounded-2xl mb-4 shadow-lg transform group-hover:rotate-12 transition-transform`}
                >
                  {stat.icon}
                </div>
                <div className='text-3xl font-black mb-1'>{stat.value}</div>
                <div className='text-sm font-bold text-gray-500 uppercase tracking-widest'>
                  {stat.label}
                </div>
              </GlassCard>
            </motion.div>
          ))}
        </div>

        {/* Carousel Section */}
        <div className='relative mb-20'>
          <AnimatePresence mode='wait'>
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, x: isAr ? -50 : 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: isAr ? 50 : -50 }}
              transition={{ duration: 0.4 }}
              className='max-w-4xl mx-auto'
            >
              <GlassCard
                lang={lang}
                className='p-10 md:p-16 border border-white/20 relative'
              >
                <Quote
                  className={`absolute top-6 ${isAr ? 'left-6' : 'right-6'} h-16 w-16 text-royal-purple-500/10`}
                />

                <p className='text-xl md:text-2xl font-medium italic text-gray-800 dark:text-gray-200 mb-10 leading-relaxed'>
                  "
                  {isAr
                    ? reviews[currentIndex].commentAr ||
                      reviews[currentIndex].comment
                    : reviews[currentIndex].comment}
                  "
                </p>

                <div
                  className={`flex flex-col md:flex-row items-center justify-between gap-6 border-t border-gray-100 dark:border-gray-800 pt-8`}
                >
                  <div className={isAr ? 'text-right' : 'text-left'}>
                    <div
                      className={`flex items-center gap-4 mb-2 ${isAr ? 'flex-row-reverse' : ''}`}
                    >
                      <div className='flex items-center gap-1'>
                        {renderStars(reviews[currentIndex].rating)}
                      </div>
                      <div className='h-6 w-px bg-gray-300 dark:bg-gray-700' />
                      <span className='font-black text-lg'>
                        {isAr
                          ? reviews[currentIndex].nameAr ||
                            reviews[currentIndex].name
                          : reviews[currentIndex].name}
                      </span>
                    </div>
                    <div className='text-gray-500 font-medium'>
                      {isAr
                        ? reviews[currentIndex].carAr ||
                          reviews[currentIndex].car
                        : reviews[currentIndex].car}{' '}
                      • {reviews[currentIndex].date}
                    </div>
                  </div>

                  {reviews[currentIndex].verified && (
                    <div className='flex items-center gap-2 px-4 py-2 bg-green-500/10 text-green-600 dark:text-green-400 rounded-full border border-green-500/20'>
                      <Award className='h-5 w-5' />
                      <span className='text-sm font-bold uppercase tracking-wider'>
                        {content.verified}
                      </span>
                    </div>
                  )}
                </div>
              </GlassCard>
            </motion.div>
          </AnimatePresence>

          {/* Navigation Controls */}
          <div
            className={`flex items-center justify-center gap-6 mt-10 ${isAr ? 'flex-row-reverse' : ''}`}
          >
            <button
              onClick={prevReview}
              className='p-4 glass-card rounded-full hover:bg-royal-purple-500 hover:text-white transition-all shadow-xl'
            >
              <ChevronLeft className={`h-6 w-6 ${isAr ? 'rotate-180' : ''}`} />
            </button>

            <div className='flex gap-3'>
              {reviews.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setAutoplay(false);
                    setCurrentIndex(index);
                  }}
                  className={`h-2.5 rounded-full transition-all duration-500 ${
                    index === currentIndex
                      ? 'w-10 premium-gradient'
                      : 'w-2.5 bg-gray-300 dark:bg-gray-700'
                  }`}
                />
              ))}
            </div>

            <button
              onClick={nextReview}
              className='p-4 glass-card rounded-full hover:bg-royal-purple-500 hover:text-white transition-all shadow-xl'
            >
              <ChevronRight className={`h-6 w-6 ${isAr ? 'rotate-180' : ''}`} />
            </button>
          </div>
        </div>

        {/* Small Review Grid */}
        <div className='grid md:grid-cols-2 lg:grid-cols-3 gap-6'>
          {reviews.slice(0, 3).map((review, index) => (
            <motion.div
              key={review.id}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <GlassCard lang={lang} className='p-8 h-full flex flex-col'>
                <div className='flex items-center gap-1 mb-6'>
                  {renderStars(review.rating)}
                </div>
                <p className='text-gray-700 dark:text-gray-300 mb-8 italic flex-grow leading-relaxed'>
                  "{isAr ? review.commentAr || review.comment : review.comment}"
                </p>
                <div
                  className={`flex items-center justify-between pt-6 border-t border-gray-100 dark:border-gray-800 ${isAr ? 'flex-row-reverse' : ''}`}
                >
                  <span className='font-black'>
                    {isAr ? review.nameAr || review.name : review.name}
                  </span>
                  <span className='text-xs font-bold text-gray-400'>
                    {review.date}
                  </span>
                </div>
              </GlassCard>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Reviews;
